import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StageBadge } from '@/components/StageBadge';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { stageLabels } from '@/data/stageConfig';

export interface PendingStageChange {
  applicationId: string;
  fromStage: string;
  toStage: string;
  candidateName: string;
  status?: string;
  jobTitle?: string;
  confirmationTitle?: string;
  confirmationBody?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface StageChangeConfirmDialogProps {
  pending: PendingStageChange | null;
  onConfirm: () => void;
  onCancel: () => void;
  isPending?: boolean;
}

export function StageChangeConfirmDialog({ pending, onConfirm, onCancel, isPending }: StageChangeConfirmDialogProps) {
  const { t } = useTranslation();

  const isReject = pending?.status === 'rejected' || pending?.toStage === 'rejected';
  const isRollback = pending ? orderedIndex(pending.toStage) < orderedIndex(pending.fromStage) : false;

  const toLabel = pending ? (stageLabels[pending.toStage] || pending.toStage) : '';

  return (
    <AlertDialog open={!!pending} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {pending?.confirmationTitle
              ? pending.confirmationTitle
              : isReject
              ? t('hire.candidateDetail.rejectTitle')
              : isRollback
                ? t('hire.stageConfirm.rollbackTitle')
                : t('hire.candidateDetail.moveTo', { stage: toLabel }) + '?'}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                {pending?.confirmationBody
                  ? pending.confirmationBody
                  : isReject
                  ? t('hire.candidateDetail.rejectDesc', { name: pending?.candidateName, job: pending?.jobTitle || '' })
                  : isRollback
                    ? t('hire.stageConfirm.rollbackDesc', { name: pending?.candidateName })
                    : t('hire.candidateDetail.moveConfirm', { name: pending?.candidateName, stage: toLabel })}
              </p>
              {pending && (
                <div className="flex items-center gap-2 justify-center pt-1">
                  <StageBadge stage={pending.fromStage} />
                  <ChevronRight className="w-4 h-4 text-muted-foreground rtl-flip" />
                  <StageBadge stage={pending.toStage} />
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{pending?.cancelLabel || t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isPending}
            className={isReject ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
          >
            {isPending
              ? t('common.pleaseWait')
              : pending?.confirmLabel
                ? pending.confirmLabel
                : isReject
                ? t('hire.candidateDetail.yesReject')
                : t('common.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const stageOrder = ['applied', 'ai_reviewed', 'shortlisted', 'interviewed', 'second_interview', 'offering', 'signing_contract', 'hired', 'rejected'];
function orderedIndex(stage: string) {
  const i = stageOrder.indexOf(stage);
  return i >= 0 ? i : 99;
}
